/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography2, withTheme } from '@coursera/cds-core';

import Phrase from 'bundles/interactive-transcript/components/Phrase';
import type Cue from 'bundles/interactive-transcript/models/Cue';
import {
  getHighlightsForCue,
  getIsCueIncludedInHighlight,
} from 'bundles/interactive-transcript/utils/TranscriptHighlighterUtils';
import { formatTime, getTimeFromDuration } from 'bundles/interactive-transcript/utils/TranscriptUtils';
import type { Highlight } from 'bundles/video-highlighting/types';

import _t from 'i18n!nls/interactive-transcript';

export const styles = () => css`
  .timestamp {
    border: none;
    height: 26px;
    color: #666;
    font-size: 12px;
    line-height: 26px;
    margin-right: 30px;
    margin-left: -7px;
    background: white;

    &:focus {
      color: #333;
      outline: 2px solid #888;
      outline-offset: -5px;
    }

    &:hover {
      color: #333;
      text-decoration: underline;
    }
  }
`;

type Props = {
  cues: Array<Cue>;
  onCueClick: (cue: Cue) => void;
  onPhraseFocus?: () => void;
  activeCues: Array<Cue>;
  highlights: Array<Highlight>;
  targetedHighlight?: Highlight | null;
  hideTime?: boolean;
  renderPlainText?: boolean;
  theme: Theme;
  shouldDisableSkippingForward: boolean;
};

class Paragraph extends React.Component<Props> {
  handleTimestampClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const { cues, onCueClick } = this.props;
    const firstCue = cues[0];

    onCueClick(firstCue);
  };

  render() {
    const {
      cues,
      onCueClick,
      onPhraseFocus,
      activeCues,
      targetedHighlight,
      highlights,
      hideTime,
      renderPlainText,
      shouldDisableSkippingForward,
    } = this.props;
    const firstCue = cues[0];

    return (
      <Grid container css={styles}>
        <Grid
          item
          className="rc-Paragraph"
          css={css`
            display: flex;
            padding: 0 0 var(--cds-spacing-200) 0;
          `}
        >
          {!hideTime && (
            <button
              type="button"
              className="timestamp"
              onClick={this.handleTimestampClick}
              aria-labelledby={`button-label-${firstCue.id}`}
            >
              <span id={`button-label-${firstCue.id}`} className="sr-only">
                <FormattedMessage
                  message={_t(`Play video starting at {hours}:{minutes}:{seconds} and follow transcript`)}
                  {...getTimeFromDuration(firstCue.startTimeForDisplay)}
                />
              </span>
              {formatTime(firstCue.startTimeForDisplay)}
            </button>
          )}

          <div className="phrases">
            {cues.map((cue) => {
              const isActive = activeCues && activeCues.find((activeCue) => cue.id === activeCue.id);
              const cueHighlights = getHighlightsForCue(cue, highlights);
              const isTargeted = !!(targetedHighlight && getIsCueIncludedInHighlight(cue, targetedHighlight));

              if (!renderPlainText) {
                return (
                  <Phrase
                    cue={cue}
                    key={cue.id}
                    onClick={onCueClick}
                    onFocus={onPhraseFocus}
                    isActive={!!isActive}
                    isTargeted={isTargeted}
                    highlights={cueHighlights}
                    shouldDisableSkippingForward={shouldDisableSkippingForward}
                  />
                );
              } else {
                return (
                  <div className="rc-Phrase">
                    <Typography2 component="p">{cue.text}</Typography2>
                  </div>
                );
              }
            })}
          </div>
        </Grid>
      </Grid>
    );
  }
}

export default withTheme(Paragraph);
